// ============================================
// 🪜 로그인 페이지 - HTML 스타일 버전
// ============================================
import React, { useState } from 'react';
import { auth, analytics, logEvent } from '../firebase';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup
} from 'firebase/auth';

function Login({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      if (isSignUp) {
        logEvent(analytics, 'sign_up', {
          method: 'email'
        });
        await createUserWithEmailAndPassword(auth, email, password);
      } else {
        logEvent(analytics, 'login', {
          method: 'email'
        });
        await signInWithEmailAndPassword(auth, email, password);
      }
      onLogin();
    } catch (err) {
      setError(err.message);
      logEvent(analytics, isSignUp ? 'sign_up_failed' : 'login_failed', {
        method: 'email',
        error: err.code
      });
    }
  };

  const handleGoogleLogin = async () => {
    setError('');
    try {
      logEvent(analytics, 'login', {
        method: 'google'
      });
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
      onLogin();
    } catch (err) {
      setError(err.message);
      logEvent(analytics, 'login_failed', {
        method: 'google',
        error: err.code
      });
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {/* 로고 영역 */}
        <div style={styles.logoContainer}>
          <div style={styles.logoBox}>
            <img src="/logo192.png" alt="Stair Tracker Logo" style={styles.logoImage} />
          </div>
          <div style={styles.appTitle}>
            ▲ 계단 트래커 ▲
          </div>
          <div style={styles.appSubtitle}>
            한 걸음씩, 천천히 오르기 ( •ᴗ• )
          </div>
        </div>

        {/* 구분선 */}
        <div style={styles.divider}></div>

        {/* 로그인/회원가입 타이틀 */}
        <div style={styles.formTitle}>
          {isSignUp ? '▣ 회원가입 ▣' : '▣ 로그인 ▣'}
        </div>

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.inputGroup}>
            <label style={styles.label}>이메일</label>
            <input
              type="email"
              placeholder="example@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={styles.input}
              required
            />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>비밀번호</label>
            <input
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={styles.input}
              required
            />
          </div>

          {error && (
            <div style={styles.errorBox}>
              ⚠ {error}
            </div>
          )}

          <button type="submit" style={styles.button}>
            [ {isSignUp ? '가입하기' : '로그인'} ]
          </button>

          <div style={styles.divider}></div>

          <button
            type="button"
            onClick={handleGoogleLogin}
            style={styles.googleButton}
          >
            [ Google로 로그인 ]
          </button>
        </form>

        <div style={styles.footer}>
          {isSignUp ? '이미 계정이 있나요?' : '계정이 없나요?'}
          {' '}
          <span
            onClick={() => {
              logEvent(analytics, 'toggle_auth_mode', {
                from: isSignUp ? 'signup' : 'login',
                to: isSignUp ? 'login' : 'signup'
              });
              setIsSignUp(!isSignUp);
            }}
            style={styles.link}
          >
            {isSignUp ? '로그인' : '회원가입'}
          </span>
        </div>
      </div>
    </div>
  );
}

// ============================================
// 🎨 스타일
// ============================================
const styles = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    backgroundColor: '#f5f5f5',
    padding: '16px',
  },
  card: {
    backgroundColor: '#fff',
    padding: '32px',
    border: '2px solid #000',
    width: '100%',
    maxWidth: '400px',
    fontSize: '14px',
  },

  // 로고 영역
  logoContainer: {
    textAlign: 'center',
    marginBottom: '20px',
  },
  logoBox: {
    width: '80px',
    height: '80px',
    margin: '0 auto 12px',
    border: '1px solid #000',
    padding: '8px',
    backgroundColor: '#fff',
  },
  logoImage: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  },
  appTitle: {
    fontSize: '18px',
    fontWeight: '600',
    marginBottom: '4px',
  },
  appSubtitle: {
    fontSize: '12px',
    color: '#555',
  },

  // 구분선
  divider: {
    height: '1px',
    backgroundColor: '#ddd',
    margin: '16px 0',
  },

  // 폼
  formTitle: {
    fontSize: '16px',
    fontWeight: '600',
    marginBottom: '16px',
    textAlign: 'center',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
  },
  inputGroup: {
    marginBottom: '12px',
  },
  label: {
    display: 'block',
    fontSize: '13px',
    fontWeight: '500',
    marginBottom: '4px',
  },
  input: {
    width: '100%',
    padding: '10px',
    fontSize: '14px',
    border: '1px solid #000',
    backgroundColor: '#fff',
    boxSizing: 'border-box',
  },
  errorBox: {
    padding: '10px',
    backgroundColor: '#ffe0e0',
    border: '1px solid #ff0000',
    fontSize: '13px',
    color: '#cc0000',
    marginBottom: '12px',
  },
  button: {
    width: '100%',
    padding: '12px',
    fontSize: '15px',
    fontWeight: '600',
    border: '1px solid #000',
    backgroundColor: '#808080',
    color: '#fff',
    cursor: 'pointer',
    marginBottom: '8px',
  },
  googleButton: {
    width: '100%',
    padding: '12px',
    fontSize: '15px',
    fontWeight: '500',
    border: '1px solid #000',
    backgroundColor: '#fff',
    color: '#000',
    cursor: 'pointer',
    marginTop: '8px',
  },

  // 푸터
  footer: {
    marginTop: '20px',
    textAlign: 'center',
    fontSize: '13px',
    color: '#555',
  },
  link: {
    color: '#000',
    fontWeight: '600',
    cursor: 'pointer',
    textDecoration: 'underline',
  },
};

export default Login;